import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  TablePagination,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FormControl from '@mui/material/FormControl';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { formatCurrency } from '../../../utils/formatters';

import { theme } from '../../../theme';
import { superadminService } from '../../../services/superadmin.service';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';

const StatusBadge = styled('div')(({ status, theme: muiTheme }) => {
  const styles = {
    active: {
      bg: 'rgba(34, 197, 94, 0.1)',
      color: '#15803d',
      border: '1px solid rgba(34, 197, 94, 0.2)',
    },
    desactive: {
      bg: 'rgba(239, 68, 68, 0.1)',
      color: '#b91c1c',
      border: '1px solid rgba(239, 68, 68, 0.2)',
    },
    en_attente: {
      bg: 'rgba(245, 158, 11, 0.1)',
      color: '#b45309',
      border: '1px solid rgba(245, 158, 11, 0.2)',
    },
  };
  const current = styles[status] || styles.desactive;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '8px',
    fontSize: '0.7rem',
    fontWeight: 800,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    backgroundColor: current.bg,
    color: current.color,
    border: current.border,
  };
});

const StatCard = ({ title, value, icon, color }) => (
  <Card sx={{
    height: '100%',
    borderRadius: 4,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
    transition: 'transform 0.2s',
    '&:hover': { transform: 'translateY(-4px)' }
  }}>
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          p: 1.5,
          borderRadius: 3,
          bgcolor: `${color}.50`,
          color: `${color}.main`,
          display: 'flex'
        }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
            {title}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 900, color: 'text.primary' }}>
            {value}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);
const Subscriptions = () => {
  const { i18n } = useTranslation();

  // Set dayjs locale based on current language
  useEffect(() => {
    const localeMap = {
      'fr': 'fr',
      'ar': 'ar',
      'en': 'en'
    };
    dayjs.locale(localeMap[i18n.language] || 'en');
  }, [i18n.language]);

  const getStatusLabel = (status) => {
    const statusMap = {
      'active': 'Actif',
      'desactive': 'Désactivé',
      'en_attente': 'En attente'
    };
    return statusMap[status] || status;
  };


  const getPlanStyle = (planName) => {
    const name = planName?.toLowerCase() || '';
    if (name.includes('essaie') || name.includes('trial')) return { bg: '#F1F5F9', text: '#475569', border: '#E2E8F0' };
    if (name.includes('gold')) return { bg: '#FFFBEB', text: '#92400E', border: '#FEF3C7' };
    if (name.includes('premium') || name.includes('pro')) return { bg: '#EFF6FF', text: '#1E40AF', border: '#DBEAFE' };
    if (name.includes('gratuit') || name.includes('free')) return { bg: '#F0FDF4', text: '#166534', border: '#DCFCE7' };

    // Default rotation
    const defaults = [
      { bg: '#F5F3FF', text: '#5B21B6', border: '#EDE9FE' },
      { bg: '#ECFDF5', text: '#065F46', border: '#D1FAE5' },
      { bg: '#FFF7ED', text: '#9A3412', border: '#FFEDD5' },
      { bg: '#FAF5FF', text: '#6B21A8', border: '#F3E8FF' },
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return defaults[Math.abs(hash) % defaults.length];
  };

  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [features, setFeatures] = useState([]);
  const [subs, setSubs] = useState([]);
  const [error, setError] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const showToast = (message, severity = 'success') => setToast({ open: true, message: typeof message === 'string' ? message : (Array.isArray(message) ? message[0] : JSON.stringify(message)), severity });
  const closeToast = (_, reason) => { if (reason !== 'clickaway') setToast({ ...toast, open: false }); };

  const formatError = (e, fallback) => {
    const data = e?.response?.data;
    if (data?.detail) {
      return Array.isArray(data.detail) ? data.detail[0] : data.detail;
    }
    return fallback;
  };

  const [activeTab, setActiveTab] = useState(0);
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [selectedUserOption, setSelectedUserOption] = useState(null);
  const [onlyWithoutPlan, setOnlyWithoutPlan] = useState(false);
  const [neverHadPlan, setNeverHadPlan] = useState(false);

  const [trialUsers, setTrialUsers] = useState([]);
  const [trialUsersLoading, setTrialUsersLoading] = useState(false);
  const [trialEmailFilter, setTrialEmailFilter] = useState('');
  const [trialStatusFilter, setTrialStatusFilter] = useState('');
  const [trialPage, setTrialPage] = useState(0);
  const [trialPageSize, setTrialPageSize] = useState(10);
  const [trialTotal, setTrialTotal] = useState(0);

  const [subPage, setSubPage] = useState(0);
  const [subPageSize, setSubPageSize] = useState(10);
  const [subTotal, setSubTotal] = useState(0);
  const [subStatusFilter, setSubStatusFilter] = useState('');
  const [subEmailFilter, setSubEmailFilter] = useState('');

  const [activationConfirm, setActivationConfirm] = useState({
    open: false,
    subscriptionId: null,
    existingName: '',
  });

  const searchUsers = async (query) => {
    const q = (query || '').trim();
    if (q.length < 2) { setUserOptions([]); return; }
    try {
      setUserSearchLoading(true);
      const res = await superadminService.listProfessionals({ search: q, page_size: 10 });
      const data = res?.data;
      const items = Array.isArray(data) ? data : (data?.results || []);
      setUserOptions(items);
    } catch (e) {
      setUserOptions([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const loadInitialUsers = async () => {
    try {
      setUserSearchLoading(true);
      const res = await superadminService.listProfessionals({ page_size: 10 });
      const data = res?.data;
      const items = Array.isArray(data) ? data : (data?.results || []);
      setUserOptions(items);
    } catch (e) {
      setUserOptions([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const loadSubscriptions = async () => {
    try {
      const res = await superadminService.listSubscriptions({
        page: subPage + 1,
        page_size: subPageSize,
        status: subStatusFilter || undefined,
        email: subEmailFilter || undefined,
      });
      const data = res?.data;
      setSubs(data || []);
      setSubTotal(data?.count || 0);
    } catch (e) {
      console.error("Erreur lors du chargement des abonnements:", e);
    }
  };

  const loadAll = async (planIdOverride) => {
    try {
      setError('');
      setLoading(true);
      const [plansRes] = await Promise.all([
        superadminService.listSubscriptionPlans(),
      ]);
      const p = plansRes?.data || [];
      setPlans(p);

      await loadSubscriptions();

      const pid = planIdOverride ?? (selectedPlanId || (p[0]?.id ?? ''));
      setSelectedPlanId(pid || '');
      if (pid) {
        const featsRes = await superadminService.listSubscriptionFeatures(pid);
        setFeatures(featsRes?.data || []);
      } else {
        setFeatures([]);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { loadSubscriptions(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [subPage, subPageSize, subStatusFilter, subEmailFilter]);

  const loadTrialUsers = async () => {
    try {
      setTrialUsersLoading(true);
      const res = await superadminService.listTrialUsers({
        email: trialEmailFilter,
        status: trialStatusFilter,
        no_active_plan: onlyWithoutPlan,
        never_had_plan: neverHadPlan,
        page: trialPage + 1,
        page_size: trialPageSize
      });
      const data = res?.data;
      if (data?.results) {
        setTrialUsers(data.results);
        setTrialTotal(data.count || 0);
      } else {
        setTrialUsers(Array.isArray(data) ? data : []);
        setTrialTotal(Array.isArray(data) ? data.length : 0);
      }
    } catch (e) {
      console.error('Error loading trial users', e);
    } finally {
      setTrialUsersLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 2) loadTrialUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, trialPage, trialPageSize, trialStatusFilter, onlyWithoutPlan, neverHadPlan]);

  // Handle email search with debounce or manual trigger
  useEffect(() => {
    if (activeTab === 2) {
      const timer = setTimeout(() => {
        setTrialPage(0);
        loadTrialUsers();
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialEmailFilter, onlyWithoutPlan, neverHadPlan]);

  const selectedPlan = useMemo(() => plans.find(p => String(p.id) === String(selectedPlanId)) || null, [plans, selectedPlanId]);
  const planMaxSalonsText = useMemo(() => {
    const list = Array.isArray(features) ? features : [];
    const f = list.find(x => x && x.key === 'max_salons' && x.is_enabled);
    const v = f ? f.value : undefined;
    if (v === null || typeof v === 'undefined' || v === '') return 'Infini';
    return String(v);
  }, [features]);

  // Dialog states - Plan
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [planForm, setPlanForm] = useState({ id: null, name: '', code: '', description: '', price: '0', is_active: true, plan_max_salons_text: '', plan_access_clients: '', plan_access_dashboard_global: '', plan_access_dashboard_per_salon: '', plan_manage_clients: '', plan_access_messaging: '', plan_gestion_equipe: '', plan_rdv_en_ligne: '', plan_notifications_clients: '', plan_statistiques_salon: '' });
  const openCreatePlan = () => {
    setPlanForm({
      id: null,
      name: '',
      code: '',
      description: '',
      price: '',
      is_active: true,
      plan_max_salons_text: '',
      plan_rdv_en_ligne: 'true',
      plan_gestion_equipe: 'true',
      plan_notifications_clients: 'true',
      plan_statistiques_salon: 'false',
      plan_access_clients: 'false',
      plan_access_dashboard_global: 'false',
      plan_access_dashboard_per_salon: 'false',
      plan_manage_clients: 'false',
      plan_access_messaging: 'false'
    });
    setPlanDialogOpen(true);
  };
  const openEditPlan = (plan) => {
    const defaultValues = {
      plan_rdv_en_ligne: 'true',
      plan_gestion_equipe: 'true',
      plan_notifications_clients: 'true',
      plan_statistiques_salon: 'false',
      plan_access_clients: 'false',
      plan_access_dashboard_global: 'false',
      plan_access_dashboard_per_salon: 'false',
      plan_manage_clients: 'false',
      plan_access_messaging: 'false',
      plan_max_salons_text: ''
    };

    const planFeatures = features.reduce((acc, f) => {
      if (f.key === 'max_salons') {
        acc.plan_max_salons_text = String(f.value || '');
      } else {
        const planKey = `plan_${f.key}`;
        if (planKey in defaultValues) {
          acc[planKey] = String(f.value);
        }
      }
      return acc;
    }, {});

    setPlanForm({
      id: plan.id,
      name: plan.name,
      code: plan.code,
      description: plan.description || '',
      price: String(plan.price || '0'),
      is_active: !!plan.is_active,
      ...defaultValues,
      ...planFeatures
    });
    setPlanDialogOpen(true);
  };
  const submitPlan = async () => {
    try {
      if (planForm.id) {
        // Mise à jour du plan existant
        await superadminService.updateSubscriptionPlan(planForm.id, {
          name: planForm.name.trim(),
          code: planForm.code.trim(),
          description: planForm.description.trim(),
          price: parseFloat(planForm.price || '0'),
          is_active: !!planForm.is_active,
        });

        // Préparer les fonctionnalités à mettre à jour
        const toUpsert = [];

        // Gestion du nombre maximum de salons
        if (typeof planForm.plan_max_salons_text === 'string' && planForm.plan_max_salons_text.trim() !== '') {
          const n = parseInt(planForm.plan_max_salons_text.trim(), 10);
          if (!Number.isNaN(n)) toUpsert.push({ key: 'max_salons', value: n });
        }

        // Fonctionnalités existantes
        if (planForm.plan_access_clients === 'true' || planForm.plan_access_clients === 'false')
          toUpsert.push({ key: 'access_clients', value: planForm.plan_access_clients === 'true' });

        if (planForm.plan_access_dashboard_global === 'true' || planForm.plan_access_dashboard_global === 'false')
          toUpsert.push({ key: 'access_dashboard_global', value: planForm.plan_access_dashboard_global === 'true' });

        if (planForm.plan_access_dashboard_per_salon === 'true' || planForm.plan_access_dashboard_per_salon === 'false')
          toUpsert.push({ key: 'access_dashboard_per_salon', value: planForm.plan_access_dashboard_per_salon === 'true' });

        if (planForm.plan_manage_clients === 'true' || planForm.plan_manage_clients === 'false')
          toUpsert.push({ key: 'manage_clients', value: planForm.plan_manage_clients === 'true' });

        if (planForm.plan_access_messaging === 'true' || planForm.plan_access_messaging === 'false')
          toUpsert.push({ key: 'access_messaging', value: planForm.plan_access_messaging === 'true' });

        // Nouvelles fonctionnalités
        if (planForm.plan_rdv_en_ligne === 'true' || planForm.plan_rdv_en_ligne === 'false')
          toUpsert.push({ key: 'rdv_en_ligne', value: planForm.plan_rdv_en_ligne === 'true' });

        if (planForm.plan_gestion_equipe === 'true' || planForm.plan_gestion_equipe === 'false')
          toUpsert.push({ key: 'gestion_equipe', value: planForm.plan_gestion_equipe === 'true' });

        if (planForm.plan_notifications_clients === 'true' || planForm.plan_notifications_clients === 'false')
          toUpsert.push({ key: 'notifications_clients', value: planForm.plan_notifications_clients === 'true' });

        if (planForm.plan_statistiques_salon === 'true' || planForm.plan_statistiques_salon === 'false')
          toUpsert.push({ key: 'statistiques_salon', value: planForm.plan_statistiques_salon === 'true' });

        // Mettre à jour les fonctionnalités
        if (toUpsert.length) {
          const featsRes = await superadminService.listSubscriptionFeatures(planForm.id);
          const existing = featsRes?.data || [];
          await Promise.all(toUpsert.map(f => {
            const ex = existing.find(e => e.key === f.key);
            if (ex) {
              return superadminService.updateSubscriptionFeature(ex.id, {
                plan: planForm.id,
                key: f.key,
                value: f.value,
                is_enabled: true
              });
            }
            return superadminService.createSubscriptionFeature({
              plan: planForm.id,
              key: f.key,
              value: f.value,
              is_enabled: true
            });
          }));
        }

        showToast('Plan mis à jour');
      } else {
        // Création d'un nouveau plan
        const created = await superadminService.createSubscriptionPlan({
          name: planForm.name.trim(),
          code: planForm.code.trim(),
          description: planForm.description.trim(),
          price: parseFloat(planForm.price || '0'),
          is_active: !!planForm.is_active,
        });

        // Récupérer l'ID du plan créé
        let planId = created?.data?.id;
        if (!planId) {
          const plansRes = await superadminService.listSubscriptionPlans();
          const found = (plansRes?.data || []).find(x => String(x.code) === String(planForm.code).trim());
          planId = found?.id;
        }

        if (planId) {
          // Préparer les fonctionnalités à créer
          const toCreate = [];

          // Gestion du nombre maximum de salons
          if (typeof planForm.plan_max_salons_text === 'string' && planForm.plan_max_salons_text.trim() !== '') {
            const n = parseInt(planForm.plan_max_salons_text.trim(), 10);
            if (!Number.isNaN(n)) toCreate.push({ key: 'max_salons', value: n });
          }

          // Fonctionnalités existantes
          if (planForm.plan_access_clients === 'true' || planForm.plan_access_clients === 'false')
            toCreate.push({ key: 'access_clients', value: planForm.plan_access_clients === 'true' });

          if (planForm.plan_access_dashboard_global === 'true' || planForm.plan_access_dashboard_global === 'false')
            toCreate.push({ key: 'access_dashboard_global', value: planForm.plan_access_dashboard_global === 'true' });

          if (planForm.plan_access_dashboard_per_salon === 'true' || planForm.plan_access_dashboard_per_salon === 'false')
            toCreate.push({ key: 'access_dashboard_per_salon', value: planForm.plan_access_dashboard_per_salon === 'true' });

          if (planForm.plan_manage_clients === 'true' || planForm.plan_manage_clients === 'false')
            toCreate.push({ key: 'manage_clients', value: planForm.plan_manage_clients === 'true' });

          if (planForm.plan_access_messaging === 'true' || planForm.plan_access_messaging === 'false')
            toCreate.push({ key: 'access_messaging', value: planForm.plan_access_messaging === 'true' });

          // Nouvelles fonctionnalités
          if (planForm.plan_rdv_en_ligne === 'true' || planForm.plan_rdv_en_ligne === 'false')
            toCreate.push({ key: 'rdv_en_ligne', value: planForm.plan_rdv_en_ligne === 'true' });

          if (planForm.plan_gestion_equipe === 'true' || planForm.plan_gestion_equipe === 'false')
            toCreate.push({ key: 'gestion_equipe', value: planForm.plan_gestion_equipe === 'true' });

          if (planForm.plan_notifications_clients === 'true' || planForm.plan_notifications_clients === 'false')
            toCreate.push({ key: 'notifications_clients', value: planForm.plan_notifications_clients === 'true' });

          if (planForm.plan_statistiques_salon === 'true' || planForm.plan_statistiques_salon === 'false')
            toCreate.push({ key: 'statistiques_salon', value: planForm.plan_statistiques_salon === 'true' });

          // Créer les fonctionnalités
          if (toCreate.length) {
            await Promise.all(
              toCreate.map(f =>
                superadminService.createSubscriptionFeature({
                  plan: planId,
                  key: f.key,
                  value: f.value,
                  is_enabled: true
                })
              )
            );
          }
        }

        showToast('Plan créé');
      }
      setPlanDialogOpen(false);
      await loadAll(planForm.id ? planForm.id : undefined);
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Erreur lors de la sauvegarde du plan', 'error');
    }
  };
  const deletePlan = async (id) => {
    if (!window.confirm('Supprimer ce plan ?')) return;
    try {
      await superadminService.deleteSubscriptionPlan(id);
      showToast('Plan supprimé');
      await loadAll();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Erreur lors de la suppression du plan', 'error');
    }
  };

  // Dialog states - Feature
  const [featureDialogOpen, setFeatureDialogOpen] = useState(false);
  const [featureForm, setFeatureForm] = useState({ id: null, key: '', valueText: '', is_enabled: true });
  const openCreateFeature = () => { setFeatureForm({ id: null, key: '', valueText: '', is_enabled: true }); setFeatureDialogOpen(true); };
  const openEditFeature = (f) => { setFeatureForm({ id: f.id, key: f.key, valueText: JSON.stringify(f.value), is_enabled: !!f.is_enabled }); setFeatureDialogOpen(true); };
  const submitFeature = async () => {
    if (!selectedPlan) { showToast('Sélectionnez un plan', 'error'); return; }
    let parsed;
    try {
      parsed = featureForm.valueText === '' ? null : JSON.parse(featureForm.valueText);
    } catch (e) {
      showToast('Valeur invalide: utilisez du JSON valide (ex: true, 1, "abc", {"k":1})', 'error');
      return;
    }
    try {
      if (featureForm.id) {
        await superadminService.updateSubscriptionFeature(featureForm.id, {
          plan: selectedPlan.id,
          key: featureForm.key.trim(),
          value: parsed,
          is_enabled: !!featureForm.is_enabled,
        });
        showToast('Option mise à jour');
      } else {
        await superadminService.createSubscriptionFeature({
          plan: selectedPlan.id,
          key: featureForm.key.trim(),
          value: parsed,
          is_enabled: !!featureForm.is_enabled,
        });
        showToast('Option ajoutée');
      }
      setFeatureDialogOpen(false);
      const featsRes = await superadminService.listSubscriptionFeatures(selectedPlan.id);
      setFeatures(featsRes?.data || []);
    } catch (e) {
      showToast(e?.response?.data?.detail || "Erreur lors de l'enregistrement de l'option", 'error');
    }
  };
  const deleteFeature = async (id) => {
    if (!window.confirm("Supprimer cette option ?")) return;
    try {
      await superadminService.deleteSubscriptionFeature(id);
      showToast('Option supprimée');
      if (selectedPlan) {
        const featsRes = await superadminService.listSubscriptionFeatures(selectedPlan.id);
        setFeatures(featsRes?.data || []);
      }
    } catch (e) {
      showToast(e?.response?.data?.detail || "Erreur lors de la suppression de l'option", 'error');
    }
  };

  // Subscriptions management
  const [subForm, setSubForm] = useState({
    user_id: '',
    plan_id: '',
    status: 'active',
    start_date_text: '',
    duration_code: '',
  });

  const createSub = async () => {
    try {
      if (!subForm.user_id) { showToast('Sélectionnez un utilisateur', 'error'); return; }
      // If creating an active subscription, prevent duplicates for the same user
      if (subForm.status === 'active') {
        const existing = Array.isArray(subs) ? subs : (subs?.results || []);
        const hasActive = existing.some((x) => Number(x.user) === Number(subForm.user_id) && !!x.is_active);
        if (hasActive) {
          showToast("Cet utilisateur a déjà un abonnement actif. Modifiez l'existant ou désactivez-le avant d'en créer un nouveau.", 'error');
          return;
        }
      }
      if (!subForm.start_date_text || !subForm.duration_code) { showToast('Sélectionnez la date de début et la durée', 'error'); return; }
      const startISO = dayjs(subForm.start_date_text).startOf('day').toISOString();
      let d = dayjs(subForm.start_date_text);
      if (subForm.duration_code === '1m') d = d.add(1, 'month');
      else if (subForm.duration_code === '3m') d = d.add(3, 'month');
      else if (subForm.duration_code === '6m') d = d.add(6, 'month');
      else if (subForm.duration_code === '1y') d = d.add(1, 'year');
      const endISO = d.endOf('day').toISOString();
      await superadminService.createSubscription({
        user: Number(subForm.user_id),
        plan: Number(subForm.plan_id || selectedPlanId),
        status: subForm.status || 'active',
        start_date: startISO,
        end_date: endISO,
      });
      showToast('Abonnement créé');
      setSubForm({
        user_id: '',
        plan_id: '',
        status: 'active',
        start_date_text: '',
        duration_code: '',
      });
      setSelectedUserOption(null);
      setUserOptions([]);
      await loadAll(selectedPlanId);
    } catch (e) {
      showToast(formatError(e, "Erreur lors de la création de l'abonnement"), 'error');
    }
  };
  // Fonction pour mettre à jour le statut d'un abonnement
  const updateSubscriptionStatus = async (id, newStatus) => {
    try {
      if (newStatus === 'active') {
        setActivateEndDateDialog({ open: true, subscriptionId: id, end_date_text: '' });
        return;
      } else {
        await superadminService.updateSubscription(id, { status: newStatus });
      }
      showToast(`Abonnement marqué comme ${getStatusLabel(newStatus).toLowerCase()}`);
      await loadAll(selectedPlanId);
    } catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.conflict) {
        setActivationConfirm({
          open: true,
          subscriptionId: id,
          existingName: e.response.data.existing_name,
        });
      } else {
        showToast(formatError(e, 'Erreur lors de la mise à jour du statut'), 'error');
      }
    }
  };

  const handleForceActivation = async () => {
    try {
      if (!activationConfirm.subscriptionId) return;
      const payload = { force: true };
      if (activateEndDateDialog?.end_date_text) {
        payload.end_date = dayjs(activateEndDateDialog.end_date_text).endOf('day').toISOString();
      }
      await superadminService.activateSubscription(activationConfirm.subscriptionId, payload);
      showToast('Ancien abonnement désactivé et nouveau plan activé avec succès');
      setActivationConfirm({ open: false, subscriptionId: null, existingName: '' });
      await loadAll(selectedPlanId);
    } catch (e) {
      showToast(formatError(e, "Erreur lors de l'activation forcée"), 'error');
    }
  };

  const [activateEndDateDialog, setActivateEndDateDialog] = useState({ open: false, subscriptionId: null, end_date_text: '' });
  const submitActivateWithEndDate = async () => {
    try {
      if (!activateEndDateDialog.subscriptionId) return;
      const payload = {};
      if (activateEndDateDialog.end_date_text) {
        payload.end_date = dayjs(activateEndDateDialog.end_date_text).endOf('day').toISOString();
      }
      await superadminService.activateSubscription(activateEndDateDialog.subscriptionId, payload);
      showToast('Abonnement activé');
      setActivateEndDateDialog({ open: false, subscriptionId: null, end_date_text: '' });
      await loadAll(selectedPlanId);
    } catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.conflict) {
        setActivationConfirm({
          open: true,
          subscriptionId: activateEndDateDialog.subscriptionId,
          existingName: e.response.data.existing_name,
        });
      } else {
        showToast(formatError(e, "Erreur lors de l'activation"), 'error');
      }
    }
  };

  // Fonction pour activer/désactiver un abonnement avec le nouveau système de statut
  const toggleSubscriptionStatus = async (subscription) => {
    const newStatus = subscription.status === 'active' ? 'desactive' : 'active';
    await updateSubscriptionStatus(subscription.id, newStatus);
  };

  // Edit subscription dialog
  const [editSubDialogOpen, setEditSubDialogOpen] = useState(false);
  const [editSubForm, setEditSubForm] = useState({ id: null, plan_id: '', is_active: true, start_date_text: '', end_date_text: '' });
  const openEditSub = (s) => {
    setEditSubForm({
      id: s.id,
      plan_id: s.plan,
      status: s.status || 'desactive',
      is_active: String(s.status || '').toLowerCase() === 'active',
      start_date_text: s.start_date ? dayjs(s.start_date).format('YYYY-MM-DD') : '',
      end_date_text: s.end_date ? dayjs(s.end_date).format('YYYY-MM-DD') : '',
    });
    setEditSubDialogOpen(true);
  };
  const submitEditSub = async () => {
    try {
      if (!editSubForm.id) return;
      const payload = {
        plan: editSubForm.plan_id ? Number(editSubForm.plan_id) : undefined,
        status: editSubForm.status || 'desactive',
      };
      if (editSubForm.start_date_text) {
        payload.start_date = dayjs(editSubForm.start_date_text).startOf('day').toISOString();
      }
      // Allow clearing end date by sending null when empty
      payload.end_date = editSubForm.end_date_text ? dayjs(editSubForm.end_date_text).endOf('day').toISOString() : null;

      await superadminService.updateSubscription(editSubForm.id, payload);
      showToast('Abonnement mis à jour');
      setEditSubDialogOpen(false);
      await loadAll(selectedPlanId);
    } catch (e) {
      showToast(formatError(e, "Erreur lors de la mise à jour de l'abonnement"), 'error');
    }
  };

  const subsRows = useMemo(() => (Array.isArray(subs) ? subs : (subs?.results || [])), [subs]);

  const usersWithActiveSubscription = useMemo(() => {
    const now = dayjs();
    const set = new Set();
    for (const s of subsRows) {
      const userId = Number(s?.user);
      if (!userId) continue;

      const status = String(s?.status || '').toLowerCase();
      if (status !== 'active') continue;

      const end = s?.end_date ? dayjs(s.end_date) : null;
      const notExpired = !end || end.isAfter(now);
      if (notExpired) set.add(userId);
    }
    return set;
  }, [subsRows]);

  const filteredUserOptions = useMemo(() => {
    if (!onlyWithoutPlan) return userOptions;
    return (userOptions || []).filter((u) => !usersWithActiveSubscription.has(Number(u?.id)));
  }, [onlyWithoutPlan, userOptions, usersWithActiveSubscription]);

  const subStats = useMemo(() => {
    const all = subsRows;
    return {
      active: all.filter(s => s.status === 'active').length,
      pending: all.filter(s => s.status === 'en_attente').length,
      total: all.length
    };
  }, [subsRows]);


  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', pb: 12, p: { xs: 2, md: 4 } }}>
      {/* Dynamic Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: theme.colors.text.primary, letterSpacing: '-0.02em', mb: 0.5 }}>
              Abonnements & Plans
            </Typography>
            <Typography variant="body1" sx={{ color: theme.colors.text.secondary }}>
              Gérez votre écosystème d'offres et les privilèges de vos professionnels.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Actualiser les données">
              <IconButton
                onClick={() => {
                  if (activeTab === 2) {
                    setTrialPage(0);
                    loadAll();
                    loadTrialUsers();
                  } else {
                    loadAll();
                  }
                }}
                sx={{
                  bgcolor: 'white',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: '#f8fafc' }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <StatCard title="Abonnements Actifs" value={subStats.active} color="success" icon={<CheckCircleIcon fontSize="small" />} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard title="Demandes en attente" value={subStats.pending} color="warning" icon={<RefreshIcon fontSize="small" />} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard title="Plans disponibles" value={plans.length} color="primary" icon={<AddIcon fontSize="small" />} />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', bgcolor: theme.colors.primary[500] },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 800,
              fontSize: '0.95rem',
              minHeight: 48,
              color: 'text.secondary',
              '&.Mui-selected': { color: theme.colors.primary[500] }
            },
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Tab label="Configuration des Plans" />
          <Tab label="Attributions Directes" />
          <Tab label="Utilisateurs d'essai" />
        </Tabs>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: 'center', py: 8, color: theme.colors.error[600] }}>{error}</Box>
      ) : (
        activeTab === 0 ? (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <Card sx={{
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
              }}>
                <CardHeader
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Plans d'abonnement</Typography>
                  }
                  subheader="Définissez les offres commerciales de la plateforme"
                  action={
                    <Button
                      startIcon={<AddIcon />}
                      variant="contained"
                      onClick={openCreatePlan}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 900,
                        borderRadius: 2,
                        boxShadow: 'none',
                        bgcolor: theme.colors.primary[500],
                        '&:hover': { bgcolor: theme.colors.primary[600], boxShadow: 'none' }
                      }}
                    >
                      Nouveau plan
                    </Button>
                  }
                  sx={{ p: 3, pb: 0 }}
                />
                <CardContent sx={{ p: 0 }}>
                  {plans.length === 0 ? (
                    <Box sx={{ p: 10, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography variant="body1">Aucun plan configuré pour le moment.</Typography>
                    </Box>
                  ) : (
                    <Table>
                      <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Plan</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Prix</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Statut</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {plans.map((p) => (
                          <TableRow
                            key={p.id}
                            hover
                            selected={String(p.id) === String(selectedPlanId)}
                            onClick={() => {
                              setSelectedPlanId(p.id);
                              superadminService.listSubscriptionFeatures(p.id).then(res => setFeatures(res?.data || []));
                            }}
                            sx={{
                              cursor: 'pointer',
                              '&.Mui-selected': { bgcolor: 'rgba(65, 101, 72, 0.04) !important' }
                            }}
                          >
                            <TableCell>
                              <Typography sx={{ fontWeight: 800, color: 'text.primary' }}>{p.name}</Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{p.code}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontWeight: 700 }}>{formatCurrency(p.price, 'fr-DZ', 'DZD')}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={p.is_active ? 'Public' : 'Privé'}
                                size="small"
                                sx={{
                                  bgcolor: p.is_active ? 'success.50' : 'grey.100',
                                  color: p.is_active ? 'success.main' : 'text.secondary',
                                  fontWeight: 700,
                                  fontSize: '0.65rem'
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                <Tooltip title="Éditer">
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditPlan(p); }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Supprimer">
                                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deletePlan(p.id); }}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Card sx={{
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: '#f8fafc',
                height: '100%'
              }}>
                <CardHeader
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {selectedPlan ? `Options : ${selectedPlan.name}` : 'Sélectionnez un plan'}
                    </Typography>
                  }
                  action={
                    <IconButton
                      onClick={openCreateFeature}
                      disabled={!selectedPlan}
                      sx={{ bgcolor: 'white', border: '1px solid divider' }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  }
                />
                <CardContent>
                  {!selectedPlan ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
                        Cliquez sur un plan à gauche pour gérer ses privilèges et options techniques.
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Box sx={{
                        mb: 3,
                        p: 2,
                        bgcolor: 'white',
                        borderRadius: 3,
                        border: '1px solid divider',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Capacité salons</Typography>
                        <Chip label={planMaxSalonsText} color="primary" variant="outlined" size="small" sx={{ fontWeight: 900 }} />
                      </Box>

                      {features.length === 0 ? (
                        <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
                          Aucune option spécifique configurée.
                        </Typography>
                      ) : (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Clé</TableCell>
                              <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>Valeur</TableCell>
                              <TableCell align="right"></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {features.map((f) => (
                              <TableRow key={f.id} hover>
                                <TableCell sx={{ fontWeight: 600 }}>{f.key}</TableCell>
                                <TableCell>
                                  <Box sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                    bgcolor: 'rgba(0,0,0,0.03)',
                                    p: 0.5,
                                    borderRadius: 1,
                                    display: 'inline-block'
                                  }}>
                                    {JSON.stringify(f.value)}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <IconButton size="small" onClick={() => openEditFeature(f)}>
                                      <EditIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                    <IconButton size="small" color="error" onClick={() => deleteFeature(f.id)}>
                                      <DeleteIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : activeTab === 1 ? (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                mb: 4
              }}>
                <CardHeader
                  title={<Typography variant="h6" sx={{ fontWeight: 800 }}>Nouvelle attribution directe</Typography>}
                  subheader="Activez manuellement un plan pour un responsable professionnel spécifique"
                />
                <CardContent>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Autocomplete
                        size="small"
                        options={filteredUserOptions}
                        loading={userSearchLoading}
                        value={selectedUserOption}
                        isOptionEqualToValue={(opt, val) => (opt?.id || null) === (val?.id || null)}
                        onOpen={() => { if (!userOptions.length) loadInitialUsers(); }}
                        onChange={(e, val) => {
                          setSelectedUserOption(val);
                          setSubForm((s) => ({ ...s, user_id: val ? val.id : '' }));
                        }}
                        onInputChange={(_, v, reason) => { if (reason === 'input') searchUsers(v); }}
                        filterOptions={(x) => x}
                        getOptionLabel={(opt) => {
                          if (!opt) return '';
                          return `${opt.first_name || ''} ${opt.last_name || ''} (${opt.email})`;
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Chercher un responsable"
                            variant="outlined"
                            placeholder="Nom ou Email..."
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Plan</InputLabel>
                        <Select
                          value={subForm.plan_id || selectedPlanId || ''}
                          label="Plan"
                          onChange={(e) => setSubForm((s) => ({ ...s, plan_id: e.target.value }))}
                        >
                          {plans.map((p) => (
                            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        label="Début"
                        type="date"
                        size="small"
                        fullWidth
                        value={subForm.start_date_text}
                        onChange={(e) => setSubForm((s) => ({ ...s, start_date_text: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Durée</InputLabel>
                        <Select
                          value={subForm.duration_code || ''}
                          label="Durée"
                          onChange={(e) => setSubForm((s) => ({ ...s, duration_code: e.target.value }))}
                        >
                          <MenuItem value="1m">1 mois</MenuItem>
                          <MenuItem value="3m">3 mois</MenuItem>
                          <MenuItem value="6m">6 mois</MenuItem>
                          <MenuItem value="1y">1 an</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={createSub}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 900,
                          h: '40px',
                          bgcolor: theme.colors.primary[500],
                        }}
                      >
                        Attribuer le plan
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
              }}>
                <CardHeader title={<Typography variant="h6" sx={{ fontWeight: 800 }}>Historique des attributions</Typography>} />
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                      size="small"
                      label="Rechercher par email"
                      placeholder="email@domaine.com"
                      value={subEmailFilter}
                      onChange={(e) => { setSubEmailFilter(e.target.value); setSubPage(0); }}
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <InputLabel>Statut</InputLabel>
                      <Select
                        value={subStatusFilter}
                        label="Statut"
                        onChange={(e) => { setSubStatusFilter(e.target.value); setSubPage(0); }}
                      >
                        <MenuItem value="">Tous</MenuItem>
                        <MenuItem value="active">Actif</MenuItem>
                        <MenuItem value="desactive">Désactivé</MenuItem>
                        <MenuItem value="en_attente">En attente</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  {subsRows.length > 0 ? (
                    <Table>
                      <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Utilisateur</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Plan</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Statut</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Validité</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {subsRows.map((s) => (
                          <TableRow key={s.id} hover>
                            <TableCell>
                              <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>{s.user_display || 'ID: ' + s.user}</Typography>
                              <Typography variant="caption" color="text.secondary">ID Attribution: #{s.id}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={plans.find(p => p.id === s.plan)?.name || 'Plan Inconnu'}
                                size="small"
                                sx={{
                                  fontWeight: 800,
                                  bgcolor: getPlanStyle(plans.find(p => p.id === s.plan)?.name || 'Inconnu').bg,
                                  color: getPlanStyle(plans.find(p => p.id === s.plan)?.name || 'Inconnu').text,
                                  border: `1px solid ${getPlanStyle(plans.find(p => p.id === s.plan)?.name || 'Inconnu').border}`,
                                  fontSize: '0.7rem'
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={s.status || 'desactive'}>
                                {getStatusLabel(s.status || 'desactive')}
                              </StatusBadge>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {s.start_date ? dayjs(s.start_date).format('L') : '—'} ➔ {s.end_date ? dayjs(s.end_date).format('L') : '∞'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                <Tooltip title="Modifier">
                                  <IconButton size="small" onClick={() => openEditSub(s)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={s.status === 'active' ? 'Revoquer' : 'Réactiver'}>
                                  <IconButton
                                    size="small"
                                    color={s.status === 'active' ? 'error' : 'success'}
                                    onClick={() => toggleSubscriptionStatus(s)}
                                    sx={{ bgcolor: s.status === 'active' ? 'error.50' : 'success.50' }}
                                  >
                                    {s.status === 'active' ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>Aucune attribution trouvée.</Box>
                  )}
                  {subsRows.length > 0 && (
                    <Box sx={{
                      position: 'fixed',
                      bottom: 0,
                      right: 0,
                      left: { xs: 0, md: '280px' },
                      zIndex: 1000,
                      p: 2,
                      display: 'flex',
                      justifyContent: 'center',
                      pointerEvents: 'none'
                    }}>
                      <Paper
                        elevation={3}
                        sx={{
                          pointerEvents: 'auto',
                          borderRadius: theme.borderRadius.xl,
                          border: `1px solid ${theme.colors.border.light}`,
                          overflow: 'hidden',
                          width: 'fit-content'
                        }}
                      >
                        <TablePagination
                          component="div"
                          count={subTotal}
                          page={subPage}
                          onPageChange={(_, newPage) => {
                            setSubPage(newPage);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          rowsPerPage={subPageSize}
                          onRowsPerPageChange={(e) => {
                            setSubPageSize(parseInt(e.target.value, 10));
                            setSubPage(0);
                          }}
                          labelRowsPerPage="Lignes par page"
                          sx={{ bgcolor: 'white' }}
                        />
                      </Paper>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Card sx={{
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
            }}>
              <CardHeader
                title={<Typography variant="h6" sx={{ fontWeight: 800 }}>Utilisateurs en période d'essai</Typography>}
                subheader="Suivi des nouveaux comptes professionnels en test gratuit"
                action={
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <TextField
                      placeholder="Chercher par email..."
                      size="small"
                      value={trialEmailFilter}
                      onChange={(e) => setTrialEmailFilter(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: 2 }
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={onlyWithoutPlan}
                          onChange={(e) => {
                            setOnlyWithoutPlan(e.target.checked);
                            if (e.target.checked) setNeverHadPlan(false); // Uncheck other to avoid conflict logic
                            setTrialPage(0);
                          }}
                        />
                      }
                      label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Sans plan actif</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={neverHadPlan}
                          onChange={(e) => {
                            setNeverHadPlan(e.target.checked);
                            if (e.target.checked) setOnlyWithoutPlan(false); // Uncheck other
                            setTrialPage(0);
                          }}
                        />
                      }
                      label={<Typography variant="body2" sx={{ fontWeight: 700 }}>Sans plan</Typography>}
                    />
                    <FormControl size="small" sx={{ width: 140 }}>
                      <InputLabel>Statut</InputLabel>
                      <Select
                        value={trialStatusFilter}
                        label="Statut"
                        onChange={(e) => {
                          setTrialStatusFilter(e.target.value);
                          setTrialPage(0);
                        }}
                        sx={{ bgcolor: 'white', borderRadius: 2 }}
                      >
                        <MenuItem value="">Tous</MenuItem>
                        <MenuItem value="active">Actif</MenuItem>
                        <MenuItem value="desactive">Terminé</MenuItem>
                        <MenuItem value="en_attente">En attente</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                }
              />
              <CardContent sx={{ p: 0 }}>
                {trialUsersLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
                    <CircularProgress size={32} thickness={5} sx={{ color: theme.colors.primary[500] }} />
                  </Box>
                ) : (
                  <>
                    {trialUsers.length > 0 ? (
                      <Table>
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Professionnel</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Activités</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Échéance</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>Statut Essai</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {trialUsers.map((u) => {
                            const userSub = subsRows.find(s => {
                              const isThisUser = Number(s.user) === Number(u.id);
                              const plan = plans.find(p => p.id === s.plan);
                              return isThisUser && (plan?.code === 'essaie' || plan?.code === 'trial');
                            });
                            return (
                              <TableRow key={u.id} hover>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{
                                      width: 40, height: 40, borderRadius: '50%', bgcolor: 'primary.50',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      color: 'primary.main', fontWeight: 900, fontSize: '0.9rem'
                                    }}>
                                      {(u.first_name?.[0] || u.email?.[0] || '?').toUpperCase()}
                                    </Box>
                                    <Box>
                                      <Typography sx={{ fontWeight: 800 }}>{`${u.first_name || ''} ${u.last_name || ''}`}</Typography>
                                      <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip label={`${u.salons_count} Salons`} size="small" variant="outlined" sx={{ border: '1px solid #e2e8f0', bgcolor: 'white' }} />
                                    <Chip label={`${u.appointments_count} RDV`} size="small" variant="outlined" sx={{ border: '1px solid #e2e8f0', bgcolor: 'white' }} />
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  {userSub ? (
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{dayjs(userSub.end_date).format('L')}</Typography>
                                      <Typography variant="caption" sx={{ color: dayjs(userSub.end_date).isBefore(dayjs()) ? 'error.main' : 'text.secondary' }}>
                                        {dayjs(userSub.end_date).diff(dayjs(), 'days') < 0 ? 'Expiré' : `Expire dans ${dayjs(userSub.end_date).diff(dayjs(), 'days')}j`}
                                      </Typography>
                                    </Box>
                                  ) : '—'}
                                </TableCell>
                                <TableCell>
                                  {userSub ? (
                                    <StatusBadge status={userSub.status || 'desactive'}>
                                      {getStatusLabel(userSub.status || 'desactive')}
                                    </StatusBadge>
                                  ) : (
                                    <Chip label="Aucun Essai" size="small" color="error" variant="filled" />
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    {userSub ? (
                                      <>
                                        {String(userSub.status || '').toLowerCase() === 'active' && (
                                          <Button
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            onClick={() => updateSubscriptionStatus(userSub.id, 'desactive')}
                                            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                                          >
                                            Désactiver
                                          </Button>
                                        )}
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          onClick={() => openEditSub(userSub)}
                                          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                                        >
                                          Gérer
                                        </Button>
                                      </>
                                    ) : (
                                      u?.eligible_for_trial ? (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          onClick={() => {
                                            const trialPlan = plans.find(p => p.code === 'essaie' || p.code === 'trial');
                                            setSelectedUserOption(u);
                                            setSubForm({
                                              user_id: u.id,
                                              plan_id: trialPlan?.id || '',
                                              status: 'active',
                                              start_date_text: dayjs().format('YYYY-MM-DD'),
                                              duration_code: '1m'
                                            });
                                            setActiveTab(1);
                                          }}
                                          sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2 }}
                                        >
                                          Activer essai gratuit d'un mois
                                        </Button>
                                      ) : null
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography sx={{ color: 'text.secondary' }}>Aucun utilisateur d'essai trouvé.</Typography>
                      </Box>
                    )}
                    <Box sx={{
                      position: 'fixed',
                      bottom: 0,
                      right: 0,
                      left: { xs: 0, md: '280px' },
                      zIndex: 1000,
                      p: 2,
                      display: 'flex',
                      justifyContent: 'center',
                      pointerEvents: 'none'
                    }}>
                      <Paper
                        elevation={3}
                        sx={{
                          pointerEvents: 'auto',
                          borderRadius: theme.borderRadius.xl,
                          border: `1px solid ${theme.colors.border.light}`,
                          overflow: 'hidden',
                          width: 'fit-content'
                        }}
                      >
                        <TablePagination
                          component="div"
                          count={trialTotal}
                          page={trialPage}
                          onPageChange={(_, newPage) => {
                            setTrialPage(newPage);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          rowsPerPage={trialPageSize}
                          onRowsPerPageChange={(e) => {
                            setTrialPageSize(parseInt(e.target.value, 10));
                            setTrialPage(0);
                          }}
                          labelRowsPerPage="Lignes par page"
                          sx={{ bgcolor: 'white' }}
                        />
                      </Paper>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        )
      )}

      {/* Dialogs */}
      {/* Dialogs */}
      <Dialog
        open={planDialogOpen}
        onClose={() => setPlanDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 900, fontSize: '1.4rem' }}>
          {planForm.id ? 'Éditer le plan' : 'Nouveau plan d\'abonnement'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Nom du plan" value={planForm.name} onChange={(e) => setPlanForm((s) => ({ ...s, name: e.target.value }))} size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              <TextField label="Code (ID unique)" value={planForm.code} onChange={(e) => setPlanForm((s) => ({ ...s, code: e.target.value }))} size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField label="Prix mensuel (DA)" type="number" value={planForm.price} onChange={(e) => setPlanForm((s) => ({ ...s, price: e.target.value }))} size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              <FormControlLabel
                control={<Checkbox checked={!!planForm.is_active} onChange={(e) => setPlanForm((s) => ({ ...s, is_active: e.target.checked }))} color="primary" />}
                label={<Typography sx={{ fontWeight: 700 }}>Plan Actif</Typography>}
              />
            </Box>
            <TextField label="Description publique" value={planForm.description} onChange={(e) => setPlanForm((s) => ({ ...s, description: e.target.value }))} size="small" fullWidth multiline minRows={2} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />

            <TextField
              label="Limite de salons (ex: 1, 5, empty=illimité)"
              value={planForm.plan_max_salons_text}
              onChange={(e) => setPlanForm((s) => ({ ...s, plan_max_salons_text: e.target.value }))}
              size="small"
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 800, color: 'primary.main' }}>Privilèges & Accès</Typography>
              <Grid container spacing={1}>
                {[
                  { key: 'plan_rdv_en_ligne', label: 'RDV en ligne' },
                  { key: 'plan_gestion_equipe', label: 'Gestion Équipe' },
                  { key: 'plan_notifications_clients', label: 'Notif. Clients' },
                  { key: 'plan_statistiques_salon', label: 'Statistiques' },
                  { key: 'plan_access_messaging', label: 'Messagerie' },
                  { key: 'plan_manage_clients', label: 'Fichier Clients' },
                ].map((feat) => (
                  <Grid item xs={6} key={feat.key}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={planForm[feat.key] === 'true'}
                          onChange={(e) => setPlanForm(s => ({ ...s, [feat.key]: e.target.checked ? 'true' : 'false' }))}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{feat.label}</Typography>}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setPlanDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 800, color: 'text.secondary' }}>Annuler</Button>
          <Button onClick={submitPlan} variant="contained" sx={{ textTransform: 'none', fontWeight: 900, px: 4, borderRadius: 2, bgcolor: theme.colors.primary[500] }}>
            {planForm.id ? 'Mettre à jour' : 'Créer le plan'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={featureDialogOpen} onClose={() => setFeatureDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{featureForm.id ? 'Modifier une option' : 'Nouvelle option'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Clé (ex: max_salons, access_clients)" value={featureForm.key} onChange={(e) => setFeatureForm((s) => ({ ...s, key: e.target.value }))} size="small" fullWidth />
            <TextField label={'Valeur JSON (ex: 1, true, abc)'} value={featureForm.valueText} onChange={(e) => setFeatureForm((s) => ({ ...s, valueText: e.target.value }))} size="small" fullWidth />
            <FormControlLabel control={<Checkbox checked={!!featureForm.is_enabled} onChange={(e) => setFeatureForm((s) => ({ ...s, is_enabled: e.target.checked }))} />} label="Activée" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeatureDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 800 }}>Fermer</Button>
          <Button onClick={submitFeature} variant="contained" sx={{ textTransform: 'none', fontWeight: 900 }}>{featureForm.id ? 'Enregistrer' : 'Créer'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editSubDialogOpen} onClose={() => setEditSubDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Modifier l'abonnement</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InputLabel id="edit-plan-label" sx={{ minWidth: 120 }}>Plan</InputLabel>
              <Select
                labelId="edit-plan-label"
                size="small"
                value={editSubForm.plan_id || ''}
                onChange={(e) => setEditSubForm((s) => ({ ...s, plan_id: e.target.value }))}
                displayEmpty
                sx={{ minWidth: 160 }}
              >
                <MenuItem value=""><em>Plan</em></MenuItem>
                {plans.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </Box>
            <FormControlLabel control={<Checkbox checked={!!editSubForm.is_active} onChange={(e) => setEditSubForm((s) => ({ ...s, is_active: e.target.checked, status: e.target.checked ? 'active' : 'desactive' }))} />} label="Actif" />
            <TextField label="Date de début" type="date" size="small" value={editSubForm.start_date_text} onChange={(e) => setEditSubForm((s) => ({ ...s, start_date_text: e.target.value }))} InputLabelProps={{ shrink: true }} />
            <TextField label="Date de fin" type="date" size="small" value={editSubForm.end_date_text} onChange={(e) => setEditSubForm((s) => ({ ...s, end_date_text: e.target.value }))} InputLabelProps={{ shrink: true }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSubDialogOpen(false)} sx={{ textTransform: 'none', fontWeight: 800 }}>Fermer</Button>
          <Button onClick={submitEditSub} variant="contained" sx={{ textTransform: 'none', fontWeight: 900 }}>Enregistrer</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={activationConfirm.open}
        onClose={() => setActivationConfirm({ ...activationConfirm, open: false })}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Confirmer le changement de plan</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Un abonnement est déjà actif (Plan : <strong>{activationConfirm.existingName}</strong>).
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, fontWeight: 700, color: theme.colors.primary[600] }}>
            Voulez-vous désactiver l'ancien plan pour activer immédiatement celui-ci ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setActivationConfirm({ ...activationConfirm, open: false })} sx={{ textTransform: 'none', fontWeight: 800 }}>
            Annuler
          </Button>
          <Button onClick={handleForceActivation} variant="contained" color="primary" sx={{ textTransform: 'none', fontWeight: 900 }}>
            Oui, désactiver et activer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={activateEndDateDialog.open}
        onClose={() => setActivateEndDateDialog({ open: false, subscriptionId: null, end_date_text: '' })}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Choisir la date de fin</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Sélectionnez une date de fin pour ce plan (optionnel). Laissez vide pour un plan sans date de fin.
          </Typography>
          <TextField
            fullWidth
            type="date"
            label="Date de fin"
            value={activateEndDateDialog.end_date_text}
            onChange={(e) => setActivateEndDateDialog({ ...activateEndDateDialog, end_date_text: e.target.value })}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setActivateEndDateDialog({ open: false, subscriptionId: null, end_date_text: '' })}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Annuler
          </Button>
          <Button
            onClick={submitActivateWithEndDate}
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none', fontWeight: 900 }}
          >
            Activer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={5000} onClose={closeToast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={closeToast} severity={toast.severity} variant="filled" sx={{ width: '100%', fontWeight: 700 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Subscriptions;
